import React, { useContext, useEffect, useState } from 'react';
import { INTEGRATIONS } from 'screens/Integrations/helpers/constants';
import useFormikHandler from 'screens/Integrations/hooks/useFormikHandler';
import useSubmissionAnalytics from 'screens/Integrations/hooks/useSubmissionAnalytics';
import { useHistory } from 'react-router-dom';
import SubmissionFooter from 'screens/Integrations/components/SubmissionFooter';
import StepCard from 'screens/Integrations/components/StepCard';
import { paths } from 'routes/paths';
import SpinnerModal from 'components/SpinnerModal';
import StoreIcon from 'components/Icons/StoreIcon';
import useIncomingInvoiceApi from 'screens/Integrations/hooks/useIncomingInvoiceApi';
import { Button, Tag } from '@agconnections/grow-ui';
import IntegrationSuccess from 'screens/Integrations/components/IntegrationSuccess';
import { AgVendContext } from '../context/Provider';
import AgVendInvoicesReviewTable from './components/AgVendInvoicesReviewTable';

const AgVendReviewAndImport = () => {
  const history = useHistory();
  const { events, triggerAnalyticsEvent } = useSubmissionAnalytics();

  const { gotoStep } = useContext(AgVendContext);

  const {
    loading: isLoading,
    importIncomingInvoices
  } = useIncomingInvoiceApi();

  const {
    selectedInvoices,
    setSelectedInvoices,
    selectedCropSeasons,
    excludedProductsIds
  } = useFormikHandler();

  const [invoicesByRetailer, setInvoicesByRetailer] = useState([]);
  const [totalInvoices, setTotalInvoices] = useState(selectedInvoices?.length);
  const [invoicesImported, setInvoicesImported] = useState(false);

  const splitInvoicesByRetailer = invoices => {
    const groupedByRetailer = new Map();

    invoices?.forEach(invoice => {
      const { retailerName } = invoice;
      if (!groupedByRetailer.has(retailerName)) {
        groupedByRetailer.set(retailerName, [invoice]);
      } else {
        groupedByRetailer.get(retailerName).push(invoice);
      }
    });
    return groupedByRetailer;
  };

  useEffect(() => {
    if (selectedInvoices?.length === 0) {
      gotoStep(1);
    }

    if (selectedInvoices?.length > 0) {
      const invoicesWithProducts = selectedInvoices
        ?.map(inv => ({
          ...inv,
          products: inv.products
            ?.filter(product => !excludedProductsIds?.includes(product.id))
            ?.map(product => ({
              ...product
            }))
        }))
        ?.filter(invoice => invoice.products?.length);

      setInvoicesByRetailer(splitInvoicesByRetailer(invoicesWithProducts));
      setTotalInvoices(invoicesWithProducts?.length);
    }
  }, [selectedInvoices, excludedProductsIds, gotoStep]);

  const onRemoveHandler = invoiceNumber => {
    setSelectedInvoices(
      selectedInvoices.filter(inv => inv.invoiceNumber !== invoiceNumber)
    );
  };

  useEffect(() => {
    triggerAnalyticsEvent(
      events.epic.Integrations.IncomingInvoice.invoiceReview,
      {
        vendor: INTEGRATIONS.agVend
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onImportHandler = () => {
    triggerAnalyticsEvent(
      events.epic.Integrations.IncomingInvoice.invoiceImport,
      {
        vendor: INTEGRATIONS.agVend
      }
    );
    importIncomingInvoices({
      seasonId: selectedCropSeasons[0].id,
      ids: selectedInvoices.map(invoice => invoice.id),
      excludedProductsIds
    }).then(() => {
      setInvoicesImported(true);
    });
  };

  return (
    <>
      {invoicesImported ? (
        <IntegrationSuccess
          title="Your data has been imported!"
          content={
            <p className="text-xs font-normal">
              Your selected invoices have been imported.
              <br />
              You can see them on the Invoices page.
            </p>
          }
        >
          <div className="flex flex-row">
            <div className="mr-2">
              <Button
                type="primary"
                size="lg"
                onClick={() => history.replace(paths.integrations)}
              >
                Return to Integrations Page
              </Button>
            </div>
            <Button
              type="outline"
              size="lg"
              onClick={() => history.replace(paths.invoices)}
            >
              Go to Invoices
            </Button>
          </div>
        </IntegrationSuccess>
      ) : (
        <>
          <SpinnerModal
            open={isLoading}
            message="Importing records from retailer"
          />
          <StepCard size="full" testId="agvend-incoming-invoice-review">
            <div className="flex flex-col">
              <div>
                <div className="flex flex-col">
                  <div className="text-lg leading-7 font-semibold text-neutral-1000 mb-2">
                    Review and Import
                  </div>
                  <p className="text-sm leading-6 text-neutral-70">
                    Review matched data before importing
                  </p>
                </div>
                <div className="h-px w-full bg-gray-500 mt-6 mb-6" />
                <div className="w-full flex flex-row h-15 items-center font-semibold">
                  <p className="text-2xl">
                    Invoice Import {new Date().toLocaleDateString('en-Us')}
                  </p>
                  <div className="ml-4">
                    <Tag color="#217c74">
                      <span className="p-1 text-white inline-block w-fit">
                        {totalInvoices}{' '}
                        {`${totalInvoices > 1 ? 'Invoices' : 'Invoice'} Total`}
                      </span>
                    </Tag>
                  </div>
                </div>
              </div>
              {[...invoicesByRetailer.keys()].map(key => {
                const invoices = invoicesByRetailer.get(key);
                if (invoices.length <= 0) {
                  return null;
                }

                return (
                  <div key={key}>
                    <div className="w-full flex mt-10">
                      <div className="flex flex-col justify-start w-full">
                        <div data-testid="invoice-retailer">
                          <div className="flex items-center">
                            <div className="flex flex-row items-center text-xl font-semibold mr-10">
                              <StoreIcon />
                              <span
                                className="ml-2 text-gray-700"
                                data-testid="retailer-name"
                              >
                                {key}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div
                          className="flex flex-col bg-white rounded-md mb-4 mt-4 shadow-sm w-full"
                          data-testid="agvend-invoice-review-list"
                        >
                          <AgVendInvoicesReviewTable
                            invoices={invoices}
                            handleRemove={onRemoveHandler}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </StepCard>

          <SubmissionFooter
            nextDisabled={!totalInvoices || !selectedCropSeasons?.length}
            onNext={onImportHandler}
            isLastStep
            context={AgVendContext}
            nextLabel="Import Records"
            integrationType={INTEGRATIONS.agVend}
            isBottomFixed
            displaySubmissionModal={false}
          />
        </>
      )}
    </>
  );
};

export default AgVendReviewAndImport;
